import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import CustomScrollbar from './CustomScrollbar';

interface Option {
  value: string | number;
  label: string;
}

interface CustomDropdownProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  requiredIndicatorPosition?: string;
  overrideClassName?: string;
}

const hasTextSize = (className?: string) =>
  className?.split(' ').some((c) => c.startsWith('text-'));

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  required = false,
  requiredIndicatorPosition = 'left-[calc(0.5rem+10ch)]',
  overrideClassName,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [dropdownPosition, setDropdownPosition] = React.useState('left-0');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Auto reset when no results found
  useEffect(() => {
    if (searchTerm && filteredOptions.length === 0) {
      const timer = setTimeout(() => {
        setSearchTerm('');
        onChange('');
      }, 100);
      return () => clearTimeout(timer);
    }
    // Return undefined when condition is not met
    return undefined;
  }, [searchTerm, filteredOptions.length, onChange]);

  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key.length === 1) {
        setSearchTerm((prev) => prev + event.key);
      } else if (isOpen && event.key === 'Backspace') {
        setSearchTerm((prev) => prev.slice(0, -1));
      } else if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      // Check if dropdown goes off screen and adjust position
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const dropdownWidth = 200; // Approximate dropdown width

        if (rect.left + dropdownWidth > screenWidth - 20) {
          setDropdownPosition('right-0');
        } else if (rect.left < 20) {
          setDropdownPosition('left-0');
        } else {
          setDropdownPosition('left-0');
        }
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        disabled={disabled}
        className={
          (overrideClassName?.trim()
            ? overrideClassName
            : ' flex w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D]/10  text-[#4A082D] focus:outline-none text-left flex justify-between items-center') +
          (disabled ? ' cursor-not-allowed opacity-60' : '')
        }
      >
        <span
          className={
            !selectedOption
              ? `
        italic text-[#621C43]
        ${hasTextSize(overrideClassName) ? 'text-[#A40E5F]' : 'text-base'}
      `
              : `
        text-[#4A082D]
        ${hasTextSize(overrideClassName) ? 'text-[#A40E5F]' : 'text-base'}
      `
          }
        >
          {selectedOption?.label || placeholder}
        </span>
        <span className="ml-2 flex-shrink-0 mt-1">
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {required && !value && (
        <span
          className={`absolute ${requiredIndicatorPosition} top-2 text-red-500 pointer-events-none`}
        >
          *
        </span>
      )}

      {isOpen && (
        <div
          className={`absolute ${dropdownPosition} mt-2 w-max bg-white rounded-md shadow-md z-[9999] min-w-full py-2 max-h-96 overflow-hidden`}
        >
          <CustomScrollbar>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-center">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className="flex items-center px-3 py-2 cursor-pointer hover:bg-[#F8EAF1] whitespace-nowrap"
                >
                  {option.label}
                </div>
              ))
            )}
          </CustomScrollbar>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
